import { useMemo } from 'react'

// Shared scaffolding for the by-unit KPI drill-down tables (Ship 3).
//
// BreakdownShell: the "← Back" affordance + card chrome each table
// reuses (matches VarianceScreen's rounded-2xl/border idiom).
// useSortedUnits: turns the perUnit map into a sorted row array.
// SortHeader: a clickable column header that toggles asc/desc.
// pctCell: shared signed-percent / "—" cell renderer.

export default function BreakdownShell({ title, onBack, children }) {
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-[13px] font-medium text-black/60 hover:text-blue-700"
      >
        {'←'} Back
      </button>
      <section
        className="overflow-hidden rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
        aria-label={title}
      >
        <div className="mb-3 text-sm font-medium text-black/85">{title}</div>
        <div className="overflow-x-auto">{children}</div>
      </section>
    </div>
  )
}

// Flatten { [restaurant_id]: { name, ...metrics } } → [{ id, name, ... }]
// sorted by sortKey/sortDir. Nulls always sort to the bottom so a unit
// with no comparison data doesn't masquerade as the smallest value.
export function useSortedUnits(perUnit, sortKey, sortDir) {
  return useMemo(() => {
    const rows = Object.entries(perUnit || {}).map(([id, m]) => ({ id, ...m }))
    const dir = sortDir === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      const aNull = av == null
      const bNull = bv == null
      if (aNull && bNull) return 0
      if (aNull) return 1
      if (bNull) return -1
      if (typeof av === 'string' && typeof bv === 'string') {
        return av.localeCompare(bv) * dir
      }
      return ((Number(av) || 0) - (Number(bv) || 0)) * dir
    })
    return rows
  }, [perUnit, sortKey, sortDir])
}

export function SortHeader({ label, k, sortKey, sortDir, onClick, align = 'left' }) {
  const active = sortKey === k
  const arrow = active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''
  const alignCls = align === 'right' ? 'text-right' : 'text-left'
  return (
    <th className={`py-2 ${align === 'right' ? 'pl-3' : 'pr-3'} ${alignCls} font-medium`}>
      <button
        type="button"
        onClick={() => onClick(k)}
        className={`uppercase tracking-wide ${active ? 'text-blue-700' : 'text-black/55'} hover:text-blue-700`}
      >
        {label}{arrow}
      </button>
    </th>
  )
}

export function pctCell(pct) {
  if (pct == null) return <span className="text-black/40">—</span>
  const sign = pct > 0 ? '+' : ''
  const tone = pct > 0 ? 'text-emerald-700' : pct < 0 ? 'text-red-700' : 'text-black/55'
  return <span className={tone}>{sign}{pct}%</span>
}
